'use client';

import { memo } from 'react';
import { MapPinIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Anchor } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { slugify } from '@utils/strings/slugify';
import { cn } from '@utils/cn';
import { MemoizedCandidateCardTags } from './CandidateCardTags';
import {
  McCardType,
  MC_CARD_TYPE_ALERT,
} from './cardTypes';

interface CompactJobAlertCardProps {
  alert?: ApiSchema<'JobSearchAlertData'>;
  title?: string;
  type?: McCardType;
  isSelected?: boolean;
  onSelect?: (alert?: ApiSchema<'JobSearchAlertData'>) => void;
  onEdit?: () => void;
  editHref?: string;
  locale: Locale;
  /** Overrides the computed href. Useful when the card should link outside the MC context (e.g. guest users). */
  href?: string;
  /** Location string for non-alert cards (e.g. last experience). Alert cards derive location from their model. */
  location?: string;
}

/**
 * @description Compact candidate sidebar card — used for job alerts, last experience, and next-job suggestions.
 * Matches the visual structure of CompactJobCard: title → location → tags.
 */
const CompactJobAlertCard = (props: CompactJobAlertCardProps) => {
  const {
    alert, title, type = MC_CARD_TYPE_ALERT, isSelected, onSelect, onEdit, editHref, locale,
    href: hrefOverride, location: locationProp,
  } = props;
  const { t } = useTranslation('common');

  const handleClick = () => {
    onSelect?.(alert);
  };

  // Build card title from alert filters or use the provided custom title
  const getAlertTitle = () => {
    if (title) return title;
    if (!alert) return t('job-alerts.default-title') || 'Job Alert';

    const { jobSearch } = alert;
    if (!jobSearch) return t('job-alerts.default-title') || 'Job Alert';

    if (jobSearch.keyword) return jobSearch.keyword;
    if (jobSearch.domain && jobSearch.domain.length > 0) return jobSearch.domain[0].name || t('job-alerts.default-title');
    if (jobSearch.location && jobSearch.location.length > 0) return jobSearch.location[0];
    return t('job-alerts.default-title') || 'Job Alert';
  };

  const cardTitle = getAlertTitle();

  // Alert cards derive location from the alert model; other types use the passed prop
  const locationDisplay = (() => {
    if (type === MC_CARD_TYPE_ALERT) {
      return alert?.jobSearch?.location?.length ? alert.jobSearch.location.join(', ') : undefined;
    }
    return locationProp;
  })();

  const href = hrefOverride ?? (() => {
    const keyword = type === MC_CARD_TYPE_ALERT ? alert?.jobSearch?.keyword : title;
    const loc = type === MC_CARD_TYPE_ALERT
      ? alert?.jobSearch?.location?.[0]
      : locationProp;

    if (loc) {
      const locationSlug = slugify(loc);
      const keywordSlug = keyword ? `/${slugify(keyword)}` : '';
      return getI18nLinkHref(`/mc/job/list/location-${locationSlug}${keywordSlug}`, locale);
    }
    if (keyword) {
      return getI18nLinkHref(`/mc/job/list/${slugify(keyword)}`, locale);
    }
    return getI18nLinkHref('/mc', locale);
  })();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick();
      }}
      className={cn(
        'cursor-pointer rounded-2xl border bg-surface p-3 transition-all duration-200',
        'hover:border-primary/30 hover:shadow-md',
        isSelected ? 'border-primary bg-primary/5' : 'border-input',
      )}
    >
      <div className="pl-1">
        {/* Title */}
        <Anchor
          href={href}
          styling="none"
          className="flex items-start gap-2"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <span className="line-clamp-2 text-sm font-semibold text-ink hover:text-primary">
            {cardTitle}
          </span>
        </Anchor>

        {/* Location — only shown when available */}
        {(locationDisplay) && (
        <div className="mt-1 flex items-center gap-2 text-xs text-ink-medium">
          <span className="mt-0.5 flex size-2.5 shrink-0 items-center justify-center self-start">
            <MapPinIcon className="size-3 shrink-0" />
          </span>
          <span className="line-clamp-2">{locationDisplay}</span>
        </div>
        )}
      </div>

      {/* Tags + Edit button */}
      <div className="mt-2 flex items-center justify-between gap-1">
        <MemoizedCandidateCardTags href={href} />
        {onEdit && (
          <Anchor
            href={editHref ?? '#'}
            styling="none"
            className="flex items-center gap-1 rounded-full border border-[#F0F2F4] bg-surface px-4 py-1.5 text-sm text-ink-medium hover:shadow-none"
            onClick={(e) => { e.stopPropagation(); }}
          >
            <PencilIcon className="size-3.5" />
            <span>{t('my-jobs.card.actions.edit')}</span>
          </Anchor>
        )}
      </div>
    </div>
  );
};

export const MemoizedCompactJobAlertCard = memo(CompactJobAlertCard);

